import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowRight, Tag } from 'lucide-react';

const categoryColors = {
  'Costs & Pricing': 'bg-green-100 text-green-800',
  'Legal & Compliance': 'bg-red-100 text-red-800',
  'Speed & Process': 'bg-amber-100 text-amber-800',
  'Workers & Industries': 'bg-blue-100 text-blue-800',
  'Volume & Flexibility': 'bg-indigo-100 text-indigo-800',
  'Payroll & HR': 'bg-purple-100 text-purple-800',
  'Quality & Reliability': 'bg-teal-100 text-teal-800',
  'Comparing Options': 'bg-orange-100 text-orange-800',
  'Location & Regional': 'bg-cyan-100 text-cyan-800',
  'Misconceptions & Concerns': 'bg-rose-100 text-rose-800',
  'Industry News': 'bg-gray-100 text-gray-700',
  'Career Tips': 'bg-violet-100 text-violet-800',
  'Construction': 'bg-amber-100 text-amber-800',
  'Warehouse': 'bg-blue-100 text-blue-800',
  'Agriculture': 'bg-green-100 text-green-800',
  'Transportation': 'bg-indigo-100 text-indigo-800',
  'Cold Storage': 'bg-cyan-100 text-cyan-800',
};

const BlogCard = ({ post }) => {
  const navigate = useNavigate();
  const colorClass = categoryColors[post.category] || 'bg-gray-100 text-gray-700';
  const date = new Date(post.published_at).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <article
      className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer"
      onClick={() => navigate(`/blog/${post.slug}`)}
      data-testid={`blog-card-${post.slug}`}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/blog/${post.slug}`)}
    >
      {/* Hero image */}
      {post.image_url ? (
        <div className="w-full h-48 overflow-hidden">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
      ) : (
        <div className={`h-1.5 ${colorClass.split(' ')[0].replace('100', '500')}`} />
      )}

      <div className="p-6">
        {/* Category badge & meta */}
        <div className="flex items-center justify-between mb-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
            <Tag size={12} className="mr-1" aria-hidden="true" />
            {post.category}
          </span>
          <div className="flex items-center text-xs text-gray-400">
            <Clock size={12} className="mr-1" aria-hidden="true" />
            {post.reading_time} min read
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold text-[#003366] mb-2 group-hover:text-[#004488] transition-colors duration-200 line-clamp-2">
          {post.title}
        </h3>

        {/* Excerpt */}
        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">{date}</span>
          <span className="inline-flex items-center text-sm font-semibold text-[#003366] group-hover:underline">
            Read More <ArrowRight className="ml-1 group-hover:translate-x-1 transition-transform duration-200" size={14} />
          </span>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
